import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToElement, getAssetPath } from '@/utils';
import { useDeviceFeatures } from '@/hooks/useResponsive';
import { useLanguage, getLanguageName, Language } from '@/contexts/LanguageContext';
import { getRunningNotes, getContentApiBaseUrl } from '@/services/contentApi';
import { SOCIAL_LINKS } from '@/data/socialLinks';

const NAVBAR_HEIGHT = 60;
const RUNNING_LINE_HEIGHT = 30;

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const { isMobile } = useDeviceFeatures();
  const [runningLine, setRunningLine] = useState<string>(t('runningLine')); // Fallback to translation

  const languages: Language[] = ['en', 'ta'];

  const handleLanguageChange = (langCode: Language) => {
    setLanguage(langCode);
    setIsLanguageDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  // Fetch running line: Trust Monitoring content API first, then PHP backend, then translation
  useEffect(() => {
    setRunningLine(t('runningLine'));

    const applyNotes = (notes: { en?: string; ta?: string } | null) => {
      if (notes && notes[language]) {
        setRunningLine(notes[language]!);
      }
    };

    const fetchRunningLine = async () => {
      // 1) Trust Monitoring (when VITE_CONTENT_API_URL is set)
      if (getContentApiBaseUrl()) {
        try {
          const notes = await getRunningNotes();
          if (notes) {
            applyNotes(notes);
            return;
          }
        } catch {
          // Fall through to PHP or translation
        }
      }

      // 2) Legacy PHP backend (local dev only — /api on GoDaddy is for donations)
      const isLocal =
        window.location.hostname.includes('localhost') ||
        window.location.hostname.includes('127.0.0.1');
      if (!isLocal) return;

      const apiUrl = 'http://localhost:8000/api/running-line';

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        const response = await fetch(`${apiUrl}?t=${Date.now()}`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        clearTimeout(timeoutId);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data && data.data[language]) {
            setRunningLine(data.data[language]);
          }
        }
      } catch {
        // Keep translation fallback already set above
      }
    };

    fetchRunningLine();
    // Auto-refresh when using Trust Monitoring
    if (getContentApiBaseUrl()) {
      const interval = setInterval(fetchRunningLine, 60000);
      return () => clearInterval(interval);
    }
  }, [language, t]);

  const handleDonate = () => {
    setIsMobileMenuOpen(false);
    // Use a small delay to ensure menu closes first, then scroll
    setTimeout(() => {
      const donationElement = document.getElementById('donation');
      if (donationElement) {
        const navbarHeight = NAVBAR_HEIGHT + RUNNING_LINE_HEIGHT;
        const elementPosition = donationElement.offsetTop - navbarHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth',
        });
      } else {
        // Fallback: try scrollToElement if element not found immediately
        scrollToElement('donation', NAVBAR_HEIGHT + RUNNING_LINE_HEIGHT);
      }
    }, isMobile ? 300 : 0);
  };

  const toggleMobileMenu = () => {
    setIsLanguageDropdownOpen(false);
    setIsMobileMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    setIsLanguageDropdownOpen(false);
  }, [isMobile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-language-selector]')) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLanguageDropdownOpen]);

  const trustLogo = getAssetPath('assets/logos/trust logo.png');

  return (
    <>
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: NAVBAR_HEIGHT,
          padding: 0,
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
        justifyContent: 'space-between',
          zIndex: 9999,
          width: '100%'
        }}
      >
        {/* Desktop Navbar */}
        {!isMobile && (
        <div 
          style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            padding: '0 15px',
            gap: '20px'
          }}
        >
          {/* Logo Container */}
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img
              src={trustLogo}
              alt="Diya Charitable Trust Logo"
              style={{
                height: NAVBAR_HEIGHT,
                width: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </div>
          {/* Right Section */}
          <div
            style={{
              flex: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '16px'
            }}
          >
            {/* Language Selector (Translate button) */}
            <div style={{ position: 'relative', zIndex: 10000 }} data-language-selector>
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#1C3F75',
                  border: '2px solid #1C3F75',
                  padding: '8px 18px',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  position: 'relative'
                }}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2h1.5a3 3 0 013 3V16.5a1.5 1.5 0 001.5 1.5h1.39M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z"
                  />
                </svg>
                {getLanguageName(language)}
                <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                </svg>
              </motion.button>

              <AnimatePresence>
                {isLanguageDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '8px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '8px',
                      boxShadow: '0 10px 18px rgba(0,0,0,0.18)',
                      border: '1px solid #E5E7EB',
                      overflow: 'hidden',
                      minWidth: '140px',
                      zIndex: 10001
                    }}
                  >
                    {languages.map((langCode) => (
                      <button
                        key={langCode}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLanguageChange(langCode);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 14px',
                          border: 'none',
                          backgroundColor: language === langCode ? '#F3F4F6' : '#FFFFFF',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: language === langCode ? 700 : 500,
                          color: '#1C3F75',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (language !== langCode) {
                            e.currentTarget.style.backgroundColor = '#F9FAFB';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (language !== langCode) {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                          }
                        }}
                      >
                        {getLanguageName(langCode)}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Social Icons Container */}
            <div
              style={{ 
                height: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {SOCIAL_LINKS.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    fontSize: '28px',
                    margin: '0 6px',
                    display: 'flex',
                    alignItems: 'center',
                    color: social.color
                  }}
                  aria-label={social.name}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>

            {/* Donate Button */}
            <motion.button
              onClick={handleDonate}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              style={{
                height: 'calc(100% - 14px)',
                padding: '0 20px',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#A61426',
                color: '#FFFFFF',
                cursor: 'pointer',
                border: 'none',
                fontFamily: 'inherit'
              }}
            >
              {t('donate')}
            </motion.button>
          </div>
        </div>
        )}

        {/* Mobile Navbar */}
        {isMobile && (
        <div 
          style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '0 15px',
            position: 'relative'
          }}
        >
          {/* Logo Container */}
          <div
            style={{ 
              height: '100%',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img
              src={trustLogo}
              alt="Diya Charitable Trust Logo"
              style={{
                height: NAVBAR_HEIGHT,
                width: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </div>
          
          {/* Right Side - Language Button and Menu Icon */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px' 
          }}>
            {/* Language Switch Button - Circular */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: '#1C3F75',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                fontSize: '20px',
                fontWeight: 700,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                position: 'relative',
                zIndex: 10001
              }}
              aria-label="Switch language"
              data-language-selector
            >
              🌐
            </motion.button>

            {/* Language Dropdown for Mobile */}
            <AnimatePresence>
              {isLanguageDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '60px',
                    marginTop: '8px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    minWidth: '140px',
                    zIndex: 10002
                  }}
                >
                  {languages.map((langCode) => (
                    <button
                      key={langCode}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLanguageChange(langCode);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: language === langCode ? '#F3F4F6' : '#FFFFFF',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: language === langCode ? 700 : 500,
                        color: '#1C3F75',
                        transition: 'background-color 0.2s',
                        borderBottom: langCode !== languages[languages.length - 1] ? '1px solid #F3F4F6' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (language !== langCode) {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (language !== langCode) {
                          e.currentTarget.style.backgroundColor = '#FFFFFF';
                        }
                      }}
                    >
                      {getLanguageName(langCode)}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Mobile Menu Icon */}
            <motion.button
              onClick={toggleMobileMenu}
              whileTap={{ scale: 0.95 }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1C3F75',
                minWidth: '44px',
                minHeight: '44px',
                zIndex: 100,
                position: 'relative'
              }}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </motion.button>
          </div>
        </div>
        )}

        {/* Mobile Menu */}
      {isMobile && (
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: NAVBAR_HEIGHT + RUNNING_LINE_HEIGHT,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(28, 63, 117, 0.98)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              overflow: 'hidden',
              zIndex: 9998,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
            }}
            >
            <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {/* Social Media Icons */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                  {SOCIAL_LINKS.map((social) => (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileTap={{ scale: 0.95 }}
                    style={{
                      fontSize: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      color: social.color
                    }}
                      aria-label={social.name}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>

                {/* Donate Button */}
              <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '0.5rem' }}>
                  <motion.button
                    onClick={handleDonate}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                    padding: '10px 24px',
                    borderRadius: '4px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: '#FFFFFF',
                    backgroundColor: '#A61426',
                    border: 'none',
                      cursor: 'pointer',
                    fontFamily: 'inherit'
                    }}
                  >
                    {t('donate')}
                  </motion.button>
                </div>
                {/* Language Selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem', position: 'relative', zIndex: 10000 }} data-language-selector>
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
                    }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      backgroundColor: '#FFFFFF',
                      color: '#1C3F75',
                      fontWeight: 'bold',
                      borderRadius: '999px',
                      padding: '10px 18px',
                      border: '2px solid #FFFFFF',
                      alignSelf: 'center',
                      minWidth: '180px',
                      cursor: 'pointer'
                    }}
                  >
                    {getLanguageName(language)}
                  </motion.button>
                  {isLanguageDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 18px rgba(0,0,0,0.18)',
                        border: '1px solid #E5E7EB',
                        zIndex: 10001
                      }}
                    >
                      {languages.map((langCode) => (
                        <button
                          key={langCode}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLanguageChange(langCode);
                          }}
                          style={{
                            width: '100%',
                            border: 'none',
                            backgroundColor: language === langCode ? '#F3F4F6' : '#FFFFFF',
                            padding: '10px 14px',
                            textAlign: 'left',
                            fontWeight: language === langCode ? 700 : 500,
                            color: '#1C3F75',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (language !== langCode) {
                              e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (language !== langCode) {
                              e.currentTarget.style.backgroundColor = '#FFFFFF';
                            }
                          }}
                        >
                          {getLanguageName(langCode)}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      </motion.header>

      {/* Running Line Placeholder */}
      <div
        style={{
          position: 'fixed',
          top: NAVBAR_HEIGHT,
          left: 0,
          width: '100%',
          height: RUNNING_LINE_HEIGHT,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          fontSize: '16px',
          zIndex: 9997
        }}
      >
        <div
          className="running-ticker-track"
          style={{
            display: 'flex',
            whiteSpace: 'nowrap',
            animation: 'hero-ticker 85s linear infinite',
          }}
        >
          <span style={{ color: '#FFFFFF', fontWeight: 600, marginRight: '50px' }}>
            {runningLine}
          </span>
          <span style={{ color: '#FFFFFF', fontWeight: 600, marginRight: '50px' }}>
            {runningLine}
          </span>
          <span style={{ color: '#FFFFFF', fontWeight: 600 }}>
            {runningLine}
          </span>
        </div>
      </div>

      {/* Spacer to prevent overlap with content */}
      <div style={{ height: NAVBAR_HEIGHT + RUNNING_LINE_HEIGHT }} />
    </>
  );
};

export default Header;
